import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { DashboardErrorState, DashboardNarrativePanel } from "src/components/common";
import { useDashboardNarrative } from "src/hooks/useDashboardNarrative";
import type { PaidMediaOverviewCampaignRow } from "src/services/PaidMediaOverviewAPI";
import { formatOverviewNumber } from "../../utils/paidMediaOverviewFormat";
import PaidMediaOverviewHeader, {
  type PaidMediaOverviewHeaderState,
} from "./PaidMediaOverviewHeader";
import OverviewWarningsBanner from "./OverviewWarningsBanner";
import OverviewKpiCards from "./OverviewKpiCards";
import PlatformComparisonCards from "./PlatformComparisonCards";
import OverviewInsightCards from "./OverviewInsightCards";
import SpendRevenueTrendCard from "./SpendRevenueTrendCard";
import PlatformContributionCard from "./PlatformContributionCard";
import EfficiencyRow from "./EfficiencyRow";
import PurchasesTrendCard from "./PurchasesTrendCard";
import FunnelComparisonCard from "./FunnelComparisonCard";
import InvestmentEfficiencyCard from "./InvestmentEfficiencyCard";
import QuadrantCard from "./QuadrantCard";
import RevenueValidationCard from "./RevenueValidationCard";
import AttributionConfidenceCard from "./AttributionConfidenceCard";
import CampaignLeaderboard, {
  type CampaignLeaderboardFilters,
} from "./CampaignLeaderboard";
import CampaignDetailDrawer, {
  campaignSelectionFromRow,
  campaignSelectionFromScalePoint,
  type PaidMediaOverviewCampaignSelection,
} from "./CampaignDetailDrawer";
import {
  OverviewAttributionProvider,
  toOverviewAttributionSource,
  toPaidMediaAttributionMode,
} from "../../utils/paidMediaOverviewAttributionContext";
import { overviewEfficiencyLaggard } from "../../utils/paidMediaOverviewComposition";
import { overviewPlatformLabel, overviewStatusLabel } from "../../utils/paidMediaOverviewLabels";
import { downloadCsv } from "../../utils/paidMediaCsvExport";
import { formatNumberDigits } from "src/utils/numberFormat";
import {
  usePaidMediaOverview,
  usePaidMediaOverviewCampaignDetail,
  usePaidMediaOverviewCampaigns,
} from "../../hooks";
import "./PaidMediaOverviewDashboard.scss";

const DEFAULT_RANGE_DAYS = 30;
const CAMPAIGN_PAGE_SIZE = 10;
const CAMPAIGN_SORT_BY = "revenue";
const CAMPAIGN_SORT_ORDER = "desc";
const CSV_FILENAME = "paid-media-campaign-leaderboard.csv";

// Digits are localised for the export so an Arabic session gets Arabic-Indic numerals,
// matching the other dashboards' CSVs. `formatNumberDigits` (rather than `formatNumber`)
// keeps the output free of bidi isolate marks, which Excel renders as literal glyphs.
const csvNumber = (
  value: number | null | undefined,
  language: string,
  digits?: number,
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "";
  }
  const options =
    digits === undefined
      ? {}
      : { minimumFractionDigits: digits, maximumFractionDigits: digits };
  return formatNumberDigits(value, options, language);
};

const PaidMediaOverviewDashboard: FC = () => {
  const { t, i18n } = useTranslation();

  const [headerState, setHeaderState] = useState<PaidMediaOverviewHeaderState>(() => ({
    attribution: "platform",
    granularity: "daily",
    startDate: dayjs().subtract(DEFAULT_RANGE_DAYS, "day").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    platform: "all",
    campaignId: "",
    objectiveOrNetwork: "",
    status: "",
  }));
  const [leaderboardFilters, setLeaderboardFilters] = useState<CampaignLeaderboardFilters>(() => ({
    platform: "all",
    objectiveOrNetwork: "",
    status: "",
    search: "",
  }));
  const [page, setPage] = useState(1);
  // Identity only — the drawer always refetches its own numbers, so a campaign
  // can be opened from the leaderboard row or an Efficiency vs Scale bubble.
  const [selectedCampaign, setSelectedCampaign] =
    useState<PaidMediaOverviewCampaignSelection | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const attributionMode = toPaidMediaAttributionMode(headerState.attribution);

  const patchHeaderState = useCallback((patch: Partial<PaidMediaOverviewHeaderState>) => {
    setHeaderState(current => {
      const next = { ...current, ...patch };

      if (patch.attribution !== undefined && patch.attribution !== current.attribution) {
        next.campaignId = "";
        next.objectiveOrNetwork = "";
        next.status = "";
      }

      if (next.attribution === "shopify") {
        next.campaignId = "";
        next.objectiveOrNetwork = "";
        next.status = "";
      }

      return next;
    });
    setPage(1);
  }, []);

  const overview = usePaidMediaOverview({
    startDate: headerState.startDate,
    endDate: headerState.endDate,
    granularity: headerState.granularity,
    platform: headerState.platform,
    attributionMode,
    campaignId: headerState.campaignId,
    objectiveOrNetwork: headerState.objectiveOrNetwork,
    status: headerState.status,
  });

  const availableFilters = overview.filters;
  useEffect(() => {
    if (!availableFilters) {
      return;
    }

    const patch: Partial<PaidMediaOverviewHeaderState> = {};
    if (
      headerState.campaignId &&
      !availableFilters.campaigns?.some(
        campaign => campaign?.campaign_id === headerState.campaignId
      )
    ) {
      patch.campaignId = "";
    }
    if (
      headerState.objectiveOrNetwork &&
      !availableFilters.objectives_or_networks?.includes(headerState.objectiveOrNetwork)
    ) {
      patch.objectiveOrNetwork = "";
    }
    if (
      headerState.status &&
      !availableFilters.statuses?.some(status => status?.value === headerState.status)
    ) {
      patch.status = "";
    }

    if (Object.keys(patch).length) {
      setHeaderState(current => ({ ...current, ...patch }));
      setPage(1);
    }
  }, [
    availableFilters,
    headerState.campaignId,
    headerState.objectiveOrNetwork,
    headerState.status,
  ]);

  useEffect(() => {
    if (!availableFilters) {
      return;
    }

    setLeaderboardFilters(current => {
      const patch: Partial<CampaignLeaderboardFilters> = {};
      if (
        current.objectiveOrNetwork &&
        !availableFilters.objectives_or_networks?.includes(current.objectiveOrNetwork)
      ) {
        patch.objectiveOrNetwork = "";
      }
      if (
        current.status &&
        !availableFilters.statuses?.some(status => status?.value === current.status)
      ) {
        patch.status = "";
      }

      if (!Object.keys(patch).length) {
        return current;
      }
      return { ...current, ...patch };
    });
  }, [availableFilters]);

  const campaigns = usePaidMediaOverviewCampaigns({
    startDate: headerState.startDate,
    endDate: headerState.endDate,
    granularity: headerState.granularity,
    attributionMode,
    platform: leaderboardFilters.platform,
    campaignId: headerState.campaignId,
    objectiveOrNetwork:
      leaderboardFilters.objectiveOrNetwork || headerState.objectiveOrNetwork,
    status: leaderboardFilters.status || headerState.status,
    search: leaderboardFilters.search,
    page,
    pageSize: CAMPAIGN_PAGE_SIZE,
    sortBy: CAMPAIGN_SORT_BY,
    sortOrder: CAMPAIGN_SORT_ORDER,
  });

  const campaignDetail = usePaidMediaOverviewCampaignDetail({
    platform: selectedCampaign?.platform ?? null,
    campaignId: selectedCampaign?.campaign_id ?? null,
    startDate: headerState.startDate,
    endDate: headerState.endDate,
    granularity: headerState.granularity,
    attributionMode,
  });

  const summaryPlatforms = overview.summary?.platforms ?? [];
  const trendSeries = useMemo(() => overview.trends?.series ?? [], [overview.trends]);
  const purchasesPeriodTotal = formatOverviewNumber(
    overview.summary?.combined?.find(kpi => kpi?.id === "paid-purchases")?.value
  );
  const currency = overview.currency ?? campaigns.currency;
  const efficiencyLaggard = overviewEfficiencyLaggard(overview.summary);

  const allWarnings = useMemo(() => {
    const seen = new Set<string>();
    return [...overview.warnings, ...campaigns.warnings].filter(warning => {
      if (!warning?.message) {
        return false;
      }
      const key = `${warning.code ?? ""}|${warning.message}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [overview.warnings, campaigns.warnings]);

  const handleLeaderboardFiltersChange = useCallback(
    (patch: Partial<CampaignLeaderboardFilters>) => {
      setLeaderboardFilters(current => ({ ...current, ...patch }));
      setPage(1);
    },
    []
  );

  const { fetchAllRows } = campaigns;

  const handleExport = useCallback(async () => {
    setExportError(null);

    let exportRows: PaidMediaOverviewCampaignRow[];
    try {
      exportRows = await fetchAllRows();
    } catch (reason) {
      setExportError(
        reason instanceof Error
          ? reason.message
          : t("paidMediaOverview.errors.requestFailed")
      );
      return;
    }

    downloadCsv(
      CSV_FILENAME,
      [
        t("paidMediaOverview.leaderboard.columnPlatform"),
        t("paidMediaOverview.leaderboard.columnCampaign"),
        t("paidMediaOverview.leaderboard.columnObjective"),
        t("paidMediaOverview.leaderboard.columnStartDate"),
        t("paidMediaOverview.metrics.spend"),
        t("paidMediaOverview.metrics.impressions"),
        t("paidMediaOverview.metrics.clicks"),
        t("paidMediaOverview.metrics.ctr"),
        t("paidMediaOverview.metrics.purchases"),
        t("paidMediaOverview.metrics.revenue"),
        t("paidMediaOverview.metrics.roas"),
        t("paidMediaOverview.metrics.costPerPurchase"),
        t("paidMediaOverview.metrics.spendShare"),
        t("paidMediaOverview.metrics.revenueShare"),
        t("paidMediaOverview.leaderboard.columnStatus"),
      ],
      exportRows.map(row => [
        overviewPlatformLabel(t, row?.platform),
        row?.campaign_name ?? "",
        row?.objective_or_network ?? "",
        row?.start_date ?? "",
        csvNumber(row?.spend, i18n.language),
        csvNumber(row?.impressions, i18n.language),
        csvNumber(row?.clicks, i18n.language),
        csvNumber(row?.ctr, i18n.language, 2),
        csvNumber(row?.purchases, i18n.language),
        csvNumber(row?.revenue, i18n.language),
        csvNumber(row?.roas, i18n.language, 2),
        csvNumber(row?.cost_per_purchase, i18n.language, 2),
        csvNumber(row?.spend_share, i18n.language, 1),
        csvNumber(row?.revenue_share, i18n.language, 1),
        overviewStatusLabel(t, row?.status_code, row?.status_label),
      ])
    );
  }, [fetchAllRows, t, i18n.language]);

  const narrativeData = useMemo(
    () => ({
      summary: overview.summary,
      funnel: overview.funnel,
      revenue_validation: overview.revenueValidation,
      daily_performance: trendSeries,
      attribution_mode: overview.appliedAttributionMode,
    }),
    [
      overview.summary,
      overview.funnel,
      overview.revenueValidation,
      overview.appliedAttributionMode,
      trendSeries,
    ]
  );

  const {
    narrative: overviewNarrative,
    loading: narrativeLoading,
    aiSummary,
  } = useDashboardNarrative({
    dashboardKey: "paid_media",
    tabKey: "paid_media_overview",
    startDate: headerState.startDate,
    endDate: headerState.endDate,
    data: narrativeData,
    enabled: Boolean(overview.summary),
    promptVersion: "v6",
  });

  const allErrors = [...overview.errors, campaigns.error, exportError].filter(
    (error): error is string => Boolean(error)
  );

  // Campaign leaderboard refetches on table-only filter changes; keep that
  // loading state scoped to the table so the page does not jump back to top.
  const isLoading =
    overview.summaryLoading ||
    overview.trendsLoading ||
    overview.funnelLoading ||
    overview.efficiencyScaleLoading ||
    overview.revenueValidationLoading;

  return (
    <OverviewAttributionProvider
      value={toOverviewAttributionSource(overview.appliedAttributionMode)}
    >
      <div className="paid-media-overview">
        <PaidMediaOverviewHeader
          state={headerState}
          filters={overview.filters}
          onChange={patchHeaderState}
          storeRevenueAvailable={overview.storeRevenueAvailable}
        />

        {allErrors.map(error => (
          <DashboardErrorState key={error} message={error} />
        ))}

        <OverviewWarningsBanner warnings={allWarnings} />

        {isLoading ? (
          <LoadingSpinner compact />
        ) : (
          <>
            <DashboardNarrativePanel
              narrative={overviewNarrative}
              loading={narrativeLoading}
              aiSummary={aiSummary}
            />
            <OverviewKpiCards
              combined={overview.summary?.combined ?? []}
              leadingPlatform={overview.summary?.leading_platform ?? null}
              currency={currency}
            />
            <PlatformComparisonCards platforms={summaryPlatforms} currency={currency} />
            <div className="paid-media-overview__trend-grid">
              <SpendRevenueTrendCard
                series={trendSeries}
                granularity={headerState.granularity}
                leadingPlatform={overview.summary?.leading_platform ?? null}
                currency={currency}
                loading={overview.trendsLoading}
              />
              <OverviewInsightCards
                platforms={summaryPlatforms}
                revenueContributionLeader={overview.summary?.revenue_contribution_leader ?? null}
                efficiencyGap={overview.summary?.efficiency_gap ?? null}
                efficiencyLaggard={efficiencyLaggard}
              />
            </div>
            <PlatformContributionCard
              platforms={summaryPlatforms}
              revenueContributionLeader={overview.summary?.revenue_contribution_leader ?? null}
            />
            <EfficiencyRow
              series={trendSeries}
              platforms={summaryPlatforms}
              granularity={headerState.granularity}
              roasBenchmark={overview.efficiencyScale?.benchmarks?.roas_benchmark ?? null}
              currency={currency}
              loading={overview.trendsLoading}
            />
            <FunnelComparisonCard
              funnelPlatforms={overview.funnel?.platforms ?? []}
              loading={overview.funnelLoading}
            />
            <div className="paid-media-overview__two-col-grid">
              <QuadrantCard
                platformView={overview.efficiencyScale?.platform_view ?? []}
                campaignView={overview.efficiencyScale?.campaign_view ?? []}
                benchmarks={overview.efficiencyScale?.benchmarks ?? null}
                currency={currency}
                loading={overview.efficiencyScaleLoading}
                // The drawer covers the chart, so the pointer never leaves it and Recharts
                // never fires the `mouseleave` that would hide the tooltip — it stays pinned
                // over the drawer and is still there after it closes. This holds regardless
                // of whether the drawer was opened from a bubble or from the leaderboard.
                tooltipHidden={Boolean(selectedCampaign)}
                onCampaignSelect={point => {
                  const selection = campaignSelectionFromScalePoint(point);
                  if (selection) {
                    setSelectedCampaign(selection);
                  }
                }}
              />
              <PurchasesTrendCard
                series={trendSeries}
                granularity={headerState.granularity}
                purchasesPeriodTotal={purchasesPeriodTotal}
                loading={overview.trendsLoading}
              />
            </div>
            <InvestmentEfficiencyCard
              platforms={summaryPlatforms}
              revenueContributionLeader={overview.summary?.revenue_contribution_leader ?? null}
            />
            <div className="paid-media-overview__trend-grid">
              <RevenueValidationCard
                platforms={overview.revenueValidation?.platforms ?? []}
                storeRevenueAvailable={overview.storeRevenueAvailable}
                currency={currency}
                loading={overview.revenueValidationLoading}
                campaignFilterActive={Boolean(headerState.campaignId)}
              />
              <AttributionConfidenceCard
                data={overview.attributionConfidence}
                loading={overview.attributionConfidenceLoading}
                storeUnavailable={!overview.storeRevenueAvailable}
              />
            </div>
            <CampaignLeaderboard
              rows={campaigns.rows}
              currency={currency}
              filters={leaderboardFilters}
              objectiveOptions={overview.filters?.objectives_or_networks ?? []}
              statusOptions={overview.filters?.statuses ?? []}
              currentPage={campaigns.page}
              totalPages={campaigns.totalPages}
              loading={campaigns.loading}
              exporting={campaigns.exporting}
              onFiltersChange={handleLeaderboardFiltersChange}
              onPageChange={setPage}
              onSelect={row => setSelectedCampaign(campaignSelectionFromRow(row))}
              onExport={handleExport}
            />
          </>
        )}

        <CampaignDetailDrawer
          selectedRow={selectedCampaign}
          detail={campaignDetail.detail}
          currency={campaignDetail.currency ?? currency}
          loading={campaignDetail.loading}
          error={campaignDetail.error}
          notFound={campaignDetail.notFound}
          onOpenChange={open => {
            if (!open) {
              setSelectedCampaign(null);
            }
          }}
        />
      </div>
    </OverviewAttributionProvider>
  );
};

export default PaidMediaOverviewDashboard;
